import { useState, useEffect, useMemo } from 'react';
import { useToast } from '@chakra-ui/react';
import { getContainerVansByCompany } from '../utils/containerVanUtils';
import type { ContainerVan } from '../types/containerVan';

interface UseContainerVanTypeaheadReturn {
  vans: ContainerVan[];
  filteredVans: ContainerVan[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  refreshVans: () => Promise<void>;
}

export const useContainerVanTypeahead = (companyId?: string): UseContainerVanTypeaheadReturn => {
  const [vans, setVans] = useState<ContainerVan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const toast = useToast();

  // Fetch vans when companyId changes
  const fetchVans = async () => {
    if (!companyId) {
      setVans([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const fetchedVans = await getContainerVansByCompany(companyId);
      setVans(fetchedVans);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch container vans';
      setError(errorMessage);
      console.error('Error fetching container vans:', err);
      toast({
        title: 'Error Loading Vans',
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Load vans on mount and when companyId changes
  useEffect(() => {
    fetchVans();
  }, [companyId]);

  // Filter vans based on search query
  const filteredVans = useMemo(() => {
    if (!searchQuery.trim()) {
      return vans;
    }

    const query = searchQuery.toLowerCase().trim();
    return vans.filter(van => 
      van.vanNumber.toLowerCase().includes(query)
    );
  }, [vans, searchQuery]);

  const refreshVans = async () => {
    await fetchVans();
  };

  return {
    vans,
    filteredVans,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    refreshVans
  };
};